// Team Members Tab Component

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  X,
  UserPlus,
  Users as UsersIcon,
  Shield,
  Code,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { cn, userHasTeam } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import type { Team, User, TeamMember } from '@/types/teams';
import { AddMembersModal } from '../AddMembersModal';
import { RemoveMemberModal } from '../RemoveMemberModal';
import { useToast } from '@/hooks/use-toast';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: UsersIcon,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface TeamMembersTabProps {
  readonly team: Team;
  readonly teams: Team[];
  readonly users: User[];
  readonly members: User[];
  readonly isEditMode: boolean;
  readonly onAddMembers?: (teamId: string, members: Array<Omit<TeamMember, 'assignedAt' | 'assignedBy'>>) => Promise<TeamMember[]>;
  readonly onRemoveMembers?: (teamId: string, userIds: string[]) => Promise<boolean>;
  readonly onRefresh?: () => void;
}

export function TeamMembersTab({
  team,
  teams,
  users,
  members,
  isEditMode,
  onAddMembers,
  onRemoveMembers,
  onRefresh,
}: Readonly<TeamMembersTabProps>) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [otherTeamsFilter, setOtherTeamsFilter] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<User | null>(null);

  // Filter members
  const filteredMembers = useMemo(() => {
    let result = [...members];

    // Apply status filter
    if (statusFilter === 'active') {
      result = result.filter(m => m.status === 'active');
    } else if (statusFilter === 'inactive') {
      result = result.filter(m => m.status === 'inactive');
    }

    // Apply other teams filter
    if (otherTeamsFilter.length > 0) {
      result = result.filter(m =>
        otherTeamsFilter.some(teamId => userHasTeam(m.teams, teamId))
      );
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        m =>
          m.name.toLowerCase().includes(query) ||
          m.email.toLowerCase().includes(query)
      );
    }

    // Sort by name
    result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [members, statusFilter, otherTeamsFilter, searchQuery]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedMemberIds(filteredMembers.map(m => m.id));
    } else {
      setSelectedMemberIds([]);
    }
  };

  const handleSelectMember = (memberId: string, checked: boolean) => {
    if (checked) {
      setSelectedMemberIds([...selectedMemberIds, memberId]);
    } else {
      setSelectedMemberIds(selectedMemberIds.filter(id => id !== memberId));
    }
  };

  const getOtherTeams = (member: User) => {
    return member.teams
      .map(t => typeof t === 'string' ? t : t.teamId)
      .filter(teamId => teamId !== team.id)
      .map(teamId => teams.find(t => t.id === teamId))
      .filter(Boolean) as Team[];
  };

  const getOtherTeamBadges = (member: User) => {
    const otherTeams = getOtherTeams(member);
    const visibleTeams = otherTeams.slice(0, 3);
    const remainingCount = otherTeams.length - 3;

    if (otherTeams.length === 0) {
      return <span className="text-sm text-muted-foreground">No other teams</span>;
    }

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visibleTeams.map(t => {
          const IconComponent = TEAM_ICONS[t.icon] || UsersIcon;
          return (
            <Badge
              key={t.id}
              variant="outline"
              className="gap-1"
              style={{
                backgroundColor: `${t.color}10`,
                borderColor: t.color,
                color: t.color,
              }}
            >
              <IconComponent className="h-3 w-3" />
              {t.name}
            </Badge>
          );
        })}
        {remainingCount > 0 && (
          <Badge variant="secondary">+{remainingCount} more</Badge>
        )}
      </div>
    );
  };

  // Get unique other teams for filter
  const availableOtherTeams = useMemo(() => {
    const teamIds = new Set<string>();
    members.forEach(member => {
      member.teams.forEach(t => {
        const teamId = typeof t === 'string' ? t : t.teamId;
        if (teamId !== team.id) {
          teamIds.add(teamId);
        }
      });
    });
    return teams.filter(t => teamIds.has(t.id));
  }, [members, teams, team.id]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (otherTeamsFilter.length > 0) count++;
    return count;
  }, [statusFilter, otherTeamsFilter]);

  const clearFilters = () => {
    setStatusFilter('all');
    setOtherTeamsFilter([]);
    setSearchQuery('');
  };

  const handleBulkRemove = async () => {
    if (selectedMemberIds.length === 0 || !onRemoveMembers) return;

    try {
      await onRemoveMembers(team.id, selectedMemberIds);
      toast({
        title: 'Success',
        description: `${selectedMemberIds.length} ${selectedMemberIds.length === 1 ? 'member' : 'members'} removed from ${team.name}`,
      });
      setSelectedMemberIds([]);
      onRefresh?.();
    } catch (error) {
      console.error('Failed to remove members:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove members. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAddMembersSave = () => {
    setShowAddModal(false);
    onRefresh?.();
  };

  return (
    <>
      <div className="flex-1 flex overflow-hidden h-full">
        {/* Left Filter Panel */}
        <div className="w-64 border-r bg-muted/10 flex flex-col h-full">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm mb-3">Filters</h3>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="mb-3">
                {activeFilterCount} active
              </Badge>
            )}
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-6">
              {/* Status */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                  Status
                </h4>
                <div className="space-y-1">
                  <Button
                    variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setStatusFilter('all')}
                  >
                    All Members
                  </Button>
                  <Button
                    variant={statusFilter === 'active' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setStatusFilter('active')}
                  >
                    Active
                  </Button>
                  <Button
                    variant={statusFilter === 'inactive' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setStatusFilter('inactive')}
                  >
                    Inactive
                  </Button>
                </div>
              </div>

              {/* Other Teams */}
              {availableOtherTeams.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                    Other Teams
                  </h4>
                  <div className="space-y-1">
                    {availableOtherTeams.map(t => {
                      const IconComponent = TEAM_ICONS[t.icon] || UsersIcon;
                      const isSelected = otherTeamsFilter.includes(t.id);
                      return (
                        <Button
                          key={t.id}
                          variant={isSelected ? 'secondary' : 'ghost'}
                          size="sm"
                          className="w-full justify-start gap-2"
                          onClick={() => {
                            if (isSelected) {
                              setOtherTeamsFilter(otherTeamsFilter.filter(id => id !== t.id));
                            } else {
                              setOtherTeamsFilter([...otherTeamsFilter, t.id]);
                            }
                          }}
                        >
                          <span style={{ color: t.color }}><IconComponent className="h-4 w-4" /></span>
                          <span className="flex-1 text-left">{t.name}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {activeFilterCount > 0 && (
            <div className="p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={clearFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden h-full">
          {/* Search and Actions Bar */}
          <div className="p-4 border-b flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setShowAddModal(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Members
            </Button>
          </div>

          {/* Bulk Action Toolbar */}
          {selectedMemberIds.length > 0 && (
            <div className="px-4 py-3 border-b bg-primary/5 border-primary/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedMemberIds.length} members selected</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkRemove}
                  disabled={!onRemoveMembers}
                >
                  Remove from Team
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMemberIds([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          <ScrollArea className="flex-1">
            <Table>
              <TableHeader className="sticky top-0 bg-muted z-10">
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedMemberIds.length === filteredMembers.length && filteredMembers.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="w-[30%]">Name</TableHead>
                  <TableHead className="w-[30%]">Email</TableHead>
                  <TableHead className="w-[25%]">Other Teams</TableHead>
                  <TableHead className="w-[15%]">Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white dark:bg-background">
                {filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      {members.length === 0 ? (
                        <div className="space-y-3">
                          <p>No members in this team yet</p>
                          <Button onClick={() => setShowAddModal(true)}>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Add members to get started
                          </Button>
                        </div>
                      ) : (
                        'No members found matching your filters'
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMembers.map((member) => (
                    <TableRow
                      key={member.id}
                      className="hover:bg-muted/50"
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedMemberIds.includes(member.id)}
                          onCheckedChange={(checked) => handleSelectMember(member.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              'h-2 w-2 rounded-full',
                              member.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                            )}
                          />
                          <span className="font-medium text-primary">
                            {member.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        {getOtherTeamBadges(member)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(member.createdAt), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>

          {/* Pagination */}
          <div className="p-4 border-t flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {filteredMembers.length} of {members.length} members
            </div>
          </div>
        </div>
      </div>

      {/* Add Members Modal */}
      {showAddModal && (
        <AddMembersModal
          team={team}
          currentMembers={members}
          allUsers={users}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddMembersSave}
          onAddMembers={onAddMembers}
        />
      )}

      {/* Remove Member Modal */}
      {memberToRemove && (
        <RemoveMemberModal
          user={memberToRemove}
          team={team}
          teams={teams}
          onClose={() => setMemberToRemove(null)}
          onConfirm={() => {
            setMemberToRemove(null);
            // Refresh would be handled by parent
          }}
        />
      )}
    </>
  );
}
